// El editor propiamente dicho: recibe una escena inicial ya cargada + los datos
// de la imagen origen (para guardar en M6) y monta el estado, la barra superior,
// el rail de herramientas, sus paneles, el lienzo interactivo y la toolbar
// contextual. Se separa de EdicionGrafica (que hace la carga desde la DB) para
// poder testearlo aislado con una escena mock (Lab).

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Image as ImageIcon,
  Loader2,
  PaintBucket,
  Palette,
  Redo2,
  Save,
  Shapes,
  Type,
  Undo2,
} from 'lucide-react';
import { useScene, type SceneApi } from './scene/useScene';
import { CanvasStage } from './canvas/CanvasStage';
import { CapasPanel } from './panels/CapasPanel';
import { ToolRail, type Tool } from './panels/ToolRail';
import { TextPanel } from './panels/TextPanel';
import { TextToolbar } from './panels/TextToolbar';
import { ElementosPanel } from './panels/ElementosPanel';
import { ShapeToolbar } from './panels/ShapeToolbar';
import { ImagenesPanel } from './panels/ImagenesPanel';
import { FondoPanel } from './panels/FondoPanel';
import { MarcaPanel } from './panels/MarcaPanel';
import { ImageToolbar } from './panels/ImageToolbar';
import { addUltimoTexto } from './lib/ultimosTextos';
import { cloneLayer } from './lib/factory';
import { useBrandProfile } from '../../hooks/useBrandProfile';
import { useBrandColors } from './lib/brandColors';
import { useIsDesktop } from './lib/useIsDesktop';
import { useSidebar } from '../../context/SidebarContext';
import { sileo } from 'sileo';
import { downloadScene } from './export/composite';
import { saveSceneToHistory } from './export/saveToHistory';
import type { Layer, Scene } from './scene/types';

export interface EditorSource {
  varianteId: string | null;
  formato: string | null;
  companyId: string;
}

export interface EditorShellProps {
  initialScene: Scene;
  source: EditorSource;
  /**
   * Contexto de la pantalla de resultados desde donde se abrió el editor. Si
   * viene, al Guardar se vuelve a esa pantalla mostrando la imagen editada como
   * la principal (se suma a la tanda + se preselecciona). Si no, `navigate(-1)`.
   */
  returnTo?: {
    imageIds: string[];
    companyId: string;
    sourceImageId?: string;
    /** Ruta de retorno. Default: /redes-sociales/imagenes-generadas (resultados).
     *  El historial pasa /redes-sociales/imagenes. */
    path?: string;
    /** Clave del state para reabrir la imagen. Default: openDetailImageId
     *  (resultados). El historial usa 'expandImageId'. */
    stateKey?: string;
  } | null;
}

const TOOLS: Tool[] = [
  { id: 'texto', label: 'Texto', icon: Type },
  { id: 'elementos', label: 'Elementos', icon: Shapes },
  { id: 'imagenes', label: 'Imágenes', icon: ImageIcon },
  { id: 'marca', label: 'Marca', icon: Palette },
  { id: 'fondo', label: 'Fondo', icon: PaintBucket },
];

export function EditorShell({ initialScene, source, returnTo }: EditorShellProps) {
  const api = useScene();
  const navigate = useNavigate();
  const init = api.init;
  const { activeBrand } = useBrandProfile();
  const { sidebarWidth, setMobileHideCreditsPill, setMobileHideBottomNav, setMobileRightAction, setMobileBackOverride } = useSidebar();
  const brandId = activeBrand?.id ?? null;
  const brandColors = useBrandColors(brandId);
  const logoUrl = activeBrand?.logo_url ?? null;
  const isDesktop = useIsDesktop();
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Volver: navega DIRECTO a la pantalla de imágenes generadas mostrando la imagen
  // ORIGEN (el detalle donde estaba el botón "Edición gráfica"), con replace para no
  // caer en entradas intermedias del history (el pushState del detalle mobile, que se
  // ve como "No hay imágenes para mostrar"). Fallback sin returnTo: navigate(-1).
  const onBack = () => {
    if (returnTo) {
      navigate(returnTo.path ?? '/redes-sociales/imagenes-generadas', {
        state: {
          imageIds: returnTo.imageIds,
          companyId: returnTo.companyId,
          [returnTo.stateKey ?? 'openDetailImageId']: returnTo.sourceImageId,
        },
        replace: true,
      });
    } else {
      navigate(-1);
    }
  };
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  useEffect(() => {
    init(initialScene);
  }, [initialScene, init]);

  // En mobile el editor NO usa un topbar propio (quedaría tapado por el header del
  // shell de Atlas, que es un div z-50 que captura los clicks): usa el botón de volver
  // del shell + pone sus acciones en el header vía setMobileRightAction (abajo).
  // Ocultamos el pill de créditos y la bottom-nav mientras el editor está abierto.
  useEffect(() => {
    setMobileHideCreditsPill(true);
    setMobileHideBottomNav(true);
    // El botón de volver del shell usa NUESTRO onBack (no history.back()).
    setMobileBackOverride(() => () => onBackRef.current());
    return () => {
      setMobileHideCreditsPill(false);
      setMobileHideBottomNav(false);
      setMobileBackOverride(null);
    };
  }, [setMobileHideCreditsPill, setMobileHideBottomNav, setMobileBackOverride]);

  // Atajos: copiar (⌘/Ctrl+C), pegar (⌘/Ctrl+V), duplicar (⌘/Ctrl+D) la capa
  // seleccionada. Vía refs para suscribir el listener una sola vez.
  const apiRef = useRef(api);
  apiRef.current = api;
  const editingRef = useRef(isEditing);
  editingRef.current = isEditing;
  const copiedRef = useRef<Layer | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (editingRef.current) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) {
        return;
      }
      if (!(e.metaKey || e.ctrlKey)) return;
      const a = apiRef.current;
      const key = e.key.toLowerCase();
      if (key === 'c' && a.selected) {
        e.preventDefault();
        copiedRef.current = a.selected;
      } else if (key === 'v' && copiedRef.current) {
        e.preventDefault();
        a.addLayer(cloneLayer(copiedRef.current));
      } else if (key === 'd' && a.selected) {
        e.preventDefault();
        a.addLayer(cloneLayer(a.selected));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // El `api` que reciben los PANELES (no el lienzo): en mobile, agregar un elemento
  // cierra el panel del que se agregó. El bottom-sheet tapa el lienzo entero, así que
  // sin esto ponías una forma y no la veías; como ADD_LAYER ya deja la capa nueva
  // seleccionada, al cerrar aparece con su toolbar contextual lista. En desktop NO:
  // la columna lateral no tapa nada y cerrarla obligaría a reabrirla para poner la
  // segunda forma. Se envuelve acá, en un solo lugar, para que ningún panel tenga que
  // saber si está en mobile (son 4 los que agregan: texto, elementos, imágenes, marca).
  const panelApi: SceneApi = isDesktop
    ? api
    : {
        ...api,
        addLayer: (layer: Layer) => {
          api.addLayer(layer);
          setActiveTool(null);
        },
      };

  const onDownload = async () => {
    try {
      await downloadScene(api.scene);
    } catch (e) {
      console.error('Edición gráfica: error al descargar', e);
      sileo.error({ title: 'No se pudo descargar', description: 'Reintentá en un momento.' });
    }
  };

  const onSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const newId = await saveSceneToHistory(api.scene, source);
      sileo.success({ title: 'Guardado', description: 'La imagen quedó en tu historial.' });
      if (returnTo) {
        // Volver a la pantalla de resultados mostrando la imagen editada como la
        // principal: la sumamos al FRENTE de la tanda ("Últimas generadas") y la
        // preseleccionamos como preview grande (openDetailImageId). replace: true
        // → "atrás" no reabre el editor.
        navigate(returnTo.path ?? '/redes-sociales/imagenes-generadas', {
          state: {
            imageIds: [newId, ...returnTo.imageIds],
            companyId: returnTo.companyId,
            [returnTo.stateKey ?? 'openDetailImageId']: newId,
          },
          replace: true,
        });
      } else {
        navigate(-1);
      }
    } catch (e) {
      console.error('Edición gráfica: error al guardar', e);
      sileo.error({
        title: 'No se pudo guardar',
        description: e instanceof Error ? e.message : 'Reintentá en un momento.',
      });
    } finally {
      setSaving(false);
    }
  };

  // Acciones del nav flotante (sobre la capa seleccionada).
  const onDuplicate = () => {
    if (api.selected) api.addLayer(cloneLayer(api.selected));
  };
  const onRemoveSelected = () => {
    if (api.selected) api.removeLayer(api.selected.id);
  };

  // Acciones del editor DENTRO del header del shell (mobile). Refs para que el onClick
  // no quede stale sin re-setear el nodo en cada edición; el nodo se re-setea solo
  // cuando cambian los estados visuales (canUndo/canRedo/saving/isEditing).
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const onDownloadRef = useRef(onDownload);
  onDownloadRef.current = onDownload;
  useEffect(() => {
    const b =
      'w-9 h-9 rounded-full flex items-center justify-center text-neutral-600 active:bg-neutral-100 disabled:opacity-40 disabled:pointer-events-none transition';
    setMobileRightAction(
      // `ml-auto` empuja el grupo (undo/redo/descargar/Guardar) contra el borde
      // derecho del header (el shell lo espera así, ver Sidebar.tsx:665).
      <div className="ml-auto flex items-center gap-0.5">
        <button type="button" onClick={() => apiRef.current.undo()} disabled={!api.canUndo || isEditing} className={b} aria-label="Deshacer">
          <Undo2 className="w-[18px] h-[18px]" />
        </button>
        <button type="button" onClick={() => apiRef.current.redo()} disabled={!api.canRedo || isEditing} className={b} aria-label="Rehacer">
          <Redo2 className="w-[18px] h-[18px]" />
        </button>
        <button type="button" onClick={() => onDownloadRef.current()} className={b} aria-label="Descargar">
          <Download className="w-[18px] h-[18px]" />
        </button>
        <button
          type="button"
          onClick={() => onSaveRef.current()}
          disabled={saving}
          className="h-9 px-3 rounded-md bg-brand-primary active:bg-brand-primary-hover disabled:opacity-60 text-white text-[13px] font-medium inline-flex items-center gap-1.5 shadow-sm"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar
        </button>
      </div>,
    );
    return () => setMobileRightAction(null);
  }, [api.canUndo, api.canRedo, saving, isEditing, setMobileRightAction]);

  const iconBtn =
    'w-9 h-9 rounded-md flex items-center justify-center text-neutral-600 hover:bg-neutral-100 active:scale-95 transition disabled:opacity-40 disabled:pointer-events-none';

  return (
    <div
      className="fixed top-0 right-0 left-0 md:left-[var(--eg-sb)] z-30 flex flex-col bg-surface-bg overflow-hidden"
      style={
        {
          height: 'var(--app-height, 100dvh)',
          '--eg-sb': `${sidebarWidth}px`,
          // Mobile: dejar espacio para el header del shell (que reemplaza nuestro topbar).
          paddingTop: isDesktop ? undefined : 'calc(3.5rem + env(safe-area-inset-top, 0px))',
        } as CSSProperties
      }
    >
      {/* Barra superior (solo desktop; en mobile el header del shell la reemplaza). */}
      <div className="hidden md:flex items-center gap-2 px-3 md:px-4 h-14 border-b border-neutral-200 bg-surface-card shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="w-9 h-9 rounded-full flex items-center justify-center text-neutral-600 hover:bg-neutral-100 active:scale-95 transition"
          aria-label="Volver"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-[15px] font-semibold text-brand-black/90 truncate min-w-0">Edición Canva</h1>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={api.undo} disabled={!api.canUndo || isEditing} className={iconBtn} aria-label="Deshacer" title="Deshacer">
            <Undo2 className="w-[18px] h-[18px]" />
          </button>
          <button type="button" onClick={api.redo} disabled={!api.canRedo || isEditing} className={iconBtn} aria-label="Rehacer" title="Rehacer">
            <Redo2 className="w-[18px] h-[18px]" />
          </button>
          <div className="w-px h-6 bg-neutral-200 mx-1" />
          <button
            type="button"
            onClick={onDownload}
            className="h-9 px-2.5 rounded-md text-neutral-700 hover:bg-neutral-100 text-[13px] font-medium inline-flex items-center gap-1.5 transition"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Descargar</span>
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="h-9 px-4 rounded-md bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-60 text-white text-[13px] font-medium inline-flex items-center gap-1.5 shadow-sm transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar
          </button>
        </div>
      </div>

      {/* Cuerpo: rail | panel | (toolbar + lienzo) */}
      <div className="flex-1 min-h-0 flex">
        <ToolRail tools={TOOLS} active={activeTool} onSelect={setActiveTool} orientation="side" />
        {activeTool === 'texto' && <TextPanel api={panelApi} onClose={() => setActiveTool(null)} />}
        {activeTool === 'elementos' && <ElementosPanel api={panelApi} onClose={() => setActiveTool(null)} />}
        {activeTool === 'imagenes' && (
          <ImagenesPanel
            api={panelApi}
            companyId={source.companyId}
            brandId={brandId}
            onClose={() => setActiveTool(null)}
          />
        )}
        {activeTool === 'marca' && (
          <MarcaPanel api={panelApi} brandId={brandId} brandColors={brandColors} logoUrl={logoUrl} onClose={() => setActiveTool(null)} />
        )}
        {activeTool === 'fondo' && <FondoPanel api={panelApi} brandColors={brandColors} onClose={() => setActiveTool(null)} />}
        {activeTool === 'capas' && <CapasPanel api={panelApi} onClose={() => setActiveTool(null)} />}

        <div className="flex-1 min-w-0 flex flex-col relative">
          {/* Toolbar contextual. El espacio (h-12 + 1px de borde) se reserva
              SIEMPRE para que el lienzo NO salte al seleccionar/deseleccionar.
              Sin selección queda "en blanco": transparente y con borde
              transparente → se funde con el fondo, no se ve la franja. Con
              selección se pinta (bg + borde) y aparece la barra (Esquinas/etc.),
              sin mover la imagen. */}
          <div
            className={`h-12 shrink-0 border-b ${
              api.selected ? 'border-neutral-200 bg-surface-card' : 'border-transparent'
            }`}
          >
            {api.selected?.type === 'text' && <TextToolbar api={api} brandColors={brandColors} />}
            {api.selected?.type === 'shape' && <ShapeToolbar api={api} brandColors={brandColors} />}
            {api.selected?.type === 'image' && <ImageToolbar api={api} />}
          </div>
          <div className="flex-1 min-h-0 relative flex">
            <CanvasStage
              scene={api.scene}
              select={api.select}
              checkpoint={api.checkpoint}
              updateLayer={api.updateLayer}
              onTextCommit={addUltimoTexto}
              onEditingChange={setIsEditing}
              onDuplicate={onDuplicate}
              onRemove={onRemoveSelected}
              onOpenLayers={() => setActiveTool('capas')}
            />
          </div>
        </div>
      </div>

      {/* Mobile: rail de herramientas al pie (desktop usa el rail lateral de arriba). */}
      <ToolRail tools={TOOLS} active={activeTool} onSelect={setActiveTool} orientation="bottom" />
    </div>
  );
}
